import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { DocumentViewModel } from '../../viewModels/documents/documentViewModel';
import { UploadViewModel } from '../../viewModels/documents/uploadViewModel';
import { ObjectTypes } from '../../enums/objectTypes';
import { ChecklistDocumentViewModel } from '../../models/checklists/checklistAttachmentsViewModel';
import { DocumentCategoryViewModel } from '../../viewModels/documents/documentCategoryViewModel';
import { ExternalService } from '../../../external/services/external.service';
import { ExternalAuthService } from '../../../external/services/external-auth.service';
import { DocumentAbstraction } from './document.abstraction';
import { AlertService } from '../alert.service';
import { FileChunkingUtilities } from '../../utilities/file-chunking.utilities';

/**
 * Implementation for external users
 * Uses ExternalService endpoints with externalShareId
 */
@Injectable({
  providedIn: 'root',
})
export class ExternalUserDocumentService extends DocumentAbstraction {
  constructor(
    private externalService: ExternalService,
    private externalAuthService: ExternalAuthService,
    private alertService: AlertService
  ) {
    super();
  }

  private getExternalShareId(): string | null {
    const session = this.externalAuthService.getCurrentSession();
    if (!session || !session.externalShareId) {
      return null;
    }
    return session.externalShareId;
  }

  addDocument(uploadViewModel: UploadViewModel, externalShareId?: string): Observable<DocumentViewModel> {
    if (!externalShareId) {
      return throwError(() => new Error('External share ID not provided for external user'));
    }
    return this.externalService.addDocument(externalShareId, uploadViewModel);
  }

  updateDocument(uploadViewModel: UploadViewModel): Observable<DocumentViewModel> {
    return throwError(() => new Error('Update document operation not supported for external users'));
  }

  deleteDocument(documentId: number, globalObjectId: number, globalObjectType: number): Observable<void> {
    return throwError(() => new Error('Delete document operation not supported for external users'));
  }

  getByObjectId(objectId: number, objectType: ObjectTypes, externalShareId?: string): Observable<DocumentViewModel[]> {
    if (!externalShareId) {
      console.error('External share ID not provided for external user');
      return of([]);
    }
    return this.externalService.getDocumentsByObject(
      externalShareId,
      objectId.toString(),
      objectType.toString()
    );
  }

  uploadFile(file: File, isAttachment: boolean = false, isAccountLogo: boolean = false): Observable<string> {
    return throwError(() => new Error('Direct file upload not supported for external users'));
  }

  uploadPart(part: number, originalFileName: string, file: File, externalShareId?: string): Observable<void> {
    if (!externalShareId) {
      return throwError(() => new Error('External share ID not provided for external user'));
    }
    return this.externalService.uploadDocumentPart(externalShareId, part, originalFileName, file);
  }

  completeUpload(originalFileName: string, newFileName: string, isAttachment: boolean, isAccountLogo: boolean, externalShareId?: string): Observable<string> {
    if (!externalShareId) {
      return throwError(() => new Error('External share ID not provided for external user'));
    }
    return this.externalService.completeDocumentUpload(
      externalShareId,
      originalFileName,
      newFileName,
      isAttachment,
      isAccountLogo
    );
  }

  deleteUnassignedFile(fileName: string): Observable<void> {
    return throwError(() => new Error('Delete unassigned file operation not supported for external users'));
  }

  getObjectChecklistDocuments(objectId: number, objectType: ObjectTypes): Observable<ChecklistDocumentViewModel[]> {
    const externalShareId = this.getExternalShareId();
    return this.externalService.getObjectChecklistDocuments(
      externalShareId,
      objectId,
      objectType
    );
  }

  downloadDocument(id: number): void {
    this.alertService.error('Download not available for external users');
  }

  splitUploadFile(file: File, compress: boolean = false, isAttachment: boolean = true, isAccountLogo: boolean = false, externalShareId?: string): Observable<string> {
    if (!externalShareId) {
      return throwError(() => new Error('External share ID not provided for external user'));
    }
    return FileChunkingUtilities.splitAndUploadFile(
      file,
      (part, fileName, file) => this.uploadPart(part, fileName, file, externalShareId),
      (originalFileName, newFileName, isAttachment, isAccountLogo) =>
        this.completeUpload(originalFileName, newFileName, isAttachment, isAccountLogo, externalShareId),
      isAttachment,
      isAccountLogo
    );
  }

  getDocumentCategoriesWithDocuments(externalShareId?: string): Observable<DocumentCategoryViewModel[]> {
    if (!externalShareId) {
      console.error('External share ID not provided for external user');
      return of([]);
    }
    return this.externalService.getDocumentCategoriesWithDocuments(externalShareId);
  }
}
